import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { CheckCircle2, Info, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Toast — spec'd in handoff/COMPONENT_SPECS.md.
 *
 * Mount <ToastProvider> once near the app root. Anywhere below it,
 * `useToast()` returns a `toast({ tone, title, body })` function. The
 * provider renders the actual Radix viewport at top-right by default.
 */

export type ToastTone = 'success' | 'info' | 'warning' | 'danger';

interface ToastInput {
  id?: string;
  tone: ToastTone;
  title: string;
  body?: React.ReactNode;
  duration?: number;
}

interface ToastContextValue {
  toast: (t: Omit<ToastInput, 'id'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

interface ToastProviderProps {
  children: React.ReactNode;
  duration?: number;
  swipeDirection?: 'right' | 'left' | 'up' | 'down';
}

function ToastProvider({ children, duration = 5000, swipeDirection = 'right' }: ToastProviderProps) {
  const [items, setItems] = React.useState<ToastInput[]>([]);

  const dismiss = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = React.useCallback(
    (t: Omit<ToastInput, 'id'>) => {
      const id = crypto.randomUUID();
      setItems((prev) => [...prev, { ...t, id }]);
      return id;
    },
    []
  );

  const ctx = React.useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={ctx}>
      <ToastPrimitive.Provider duration={duration} swipeDirection={swipeDirection}>
        {children}
        {items.map((t) => (
          <ToastItem
            key={t.id}
            tone={t.tone}
            title={t.title}
            body={t.body}
            duration={t.duration ?? duration}
            onOpenChange={(open) => {
              if (!open && t.id) dismiss(t.id);
            }}
          />
        ))}
        {/* z-70 — above the modal (60) and the cookie banner (50), so a toast
            raised by an action taken inside a modal is actually visible. */}
        <ToastPrimitive.Viewport className="fixed top-1/2 left-1/2 z-[70] flex w-[480px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

const TONE_ICON: Record<ToastTone, React.ReactNode> = {
  success: <CheckCircle2 className="size-6 text-success-dark" aria-hidden />,
  info: <Info className="size-6 text-info-dark" aria-hidden />,
  warning: <AlertTriangle className="size-6 text-warning" aria-hidden />,
  danger: <AlertCircle className="size-6 text-danger-dark" aria-hidden />,
};

// Each tone gets a brand-aligned border + colored glow shadow so the toast
// reads as floating in the centre of the page rather than blending into chrome.
// Warning uses the warning token, so it does not read as a brand accent. Danger/success/info use
// matching sentiment colours so the visual language is consistent.
const TONE_FRAME: Record<ToastTone, string> = {
  warning:
    'border-warning ring-2 ring-warning/30 shadow-[0_20px_50px_-10px_rgba(202,138,4,0.45),0_8px_20px_-8px_rgba(0,0,0,0.25)]',
  danger:
    'border-danger ring-2 ring-danger/30 shadow-[0_20px_50px_-10px_rgba(239,68,68,0.45),0_8px_20px_-8px_rgba(0,0,0,0.25)]',
  success:
    'border-success ring-2 ring-success/30 shadow-[0_20px_50px_-10px_rgba(16,185,129,0.45),0_8px_20px_-8px_rgba(0,0,0,0.25)]',
  info:
    'border-info ring-2 ring-info/30 shadow-[0_20px_50px_-10px_rgba(14,165,233,0.45),0_8px_20px_-8px_rgba(0,0,0,0.25)]',
};

interface ToastItemProps {
  tone: ToastTone;
  title: string;
  body?: React.ReactNode;
  duration: number;
  onOpenChange: (open: boolean) => void;
}

function ToastItem({ tone, title, body, duration, onOpenChange }: ToastItemProps) {
  return (
    <ToastPrimitive.Root
      duration={duration}
      onOpenChange={onOpenChange}
      className={cn(
        'group pointer-events-auto flex w-full items-start gap-4 rounded-xl border-2 bg-card text-foreground p-5',
        TONE_FRAME[tone],
        'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-200',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
        'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform',
        'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=end]:animate-out'
      )}
    >
      <span className="mt-0.5 shrink-0">{TONE_ICON[tone]}</span>
      <div className="flex-1 min-w-0">
        <ToastPrimitive.Title className="text-base font-semibold text-foreground leading-snug">
          {title}
        </ToastPrimitive.Title>
        {body && (
          <ToastPrimitive.Description className="mt-1 text-sm text-muted-foreground leading-relaxed line-clamp-3">
            {body}
          </ToastPrimitive.Description>
        )}
      </div>
      <ToastPrimitive.Close
        aria-label="Dismiss"
        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <X className="size-4" aria-hidden />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() must be used inside <ToastProvider>.');
  }
  return ctx;
}

export { ToastProvider, useToast };
export type { ToastInput, ToastContextValue };
